import axios from 'axios';
import { TaskListResponse, TaskResultResponse } from '../types/generation';
import { API_URL } from '../config/api';
import { 
  PostData, 
  BackendContentPlanRequest, 
  ContentPlanGenerationResponse, 
  PostPromptRequest, 
  GenerateImageRequest, 
  GenerateContentRequest,
  PromptResponse,
  GenerateImageResponse,
  GenerateContentResponse,
  RegenerateItemRequest,
  RegenerateItemResponse,
  UpdatePostRequest,
  PostUpdateData,
  DeletePostRequest,
  DeletePostResponse
} from '../models';
import { ContentPlanData } from '../components/ContentPlanTable';

export interface KnowledgeItem {
  id: string;
  name: string;
  itemType: 'file' | 'folder';
  content?: string;
  children?: KnowledgeItem[];
  parentId: string | null;
  fileType?: string;
  isShared?: boolean; // Указывает, расшарен ли документ
  isAuthor?: boolean; // Указывает, является ли текущий пользователь автором документа
  participantCount?: number; // Количество участников документа (включая автора)
  participants?: DocumentParticipant[]; // Список участников (только для детального просмотра)
  createdAt?: string; // Дата создания документа
  updatedAt?: string; // Дата последнего обновления документа
  metadata?: {
    completedTasks?: number[];
    [key: string]: any;
  };
}

export interface DocumentParticipant {
  id: string;
  userId: string;
  username: string;
  fullName: string;
  role: string; // "Author" или "Editor"
  createdAt: string;
}

export interface GeneratePostRequest {
  contentPlanId: string;
  date: string;
  format?: string;
  reelsType?: string; // "Продающий" | "Экспертный" | "Развлекательный"
}

export interface GeneratePostByIdeaRequest {
  contentPlanId: string;
  date: string;
  authorIdea: string;
  referencePostUrl?: string;
  reelsType?: string; // "Продающий" | "Экспертный" | "Развлекательный"
}

export interface GenerateWeekRequest {
  contentPlanId: string;
  additionalInfo: string;
  enableDeepResearch?: boolean;
  reelsTemplateId?: string;
  youtubeUrl?: string;
}

export interface GenerateWeekResponse {
  taskId: string;
  message: string;
}

export interface AddNewDayRequest {
  contentPlanId: string;
  date: string; // YYYY-MM-DD format
}

export interface AddNewDayResponse {
  success: boolean;
  message: string;
}

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 15 * 60 * 1000
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Переменная для предотвращения множественных выходов из системы
let isLoggingOut = false;

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && !isLoggingOut) {
      console.warn('[API] Получена ошибка 401 - токен истёк или невалиден');
      
      // Устанавливаем флаг для предотвращения повторных попыток выхода
      isLoggingOut = true;
      
      // Очищаем локальное хранилище
      localStorage.removeItem('auth_token');
      
      // Отправляем событие для принудительного выхода из системы
      const logoutEvent = new CustomEvent('force-logout', {
        detail: {
          reason: 'token_expired',
          timestamp: Date.now()
        }
      });
      document.dispatchEvent(logoutEvent);
      
      // Сбрасываем флаг через небольшую задержку
      setTimeout(() => {
        isLoggingOut = false;
      }, 1000);
    }
    return Promise.reject(error);
  }
);

export const tasksApi = {
  getTasks: (pageNumber: number = 1, pageSize: number = 20) => 
    api.get<TaskListResponse>('/tasks', { params: { pageNumber, pageSize } }),
  getTaskResult: (taskId: string) => api.get<TaskResultResponse>(`/tasks/${taskId}/result`),
  cancelTask: (taskId: string) => api.post(`/tasks/${taskId}/cancel`),
  deleteTask: (taskId: string) => api.delete(`/tasks/${taskId}`),
  getUnviewedTasks: () => api.get<Array<{
    id: string;
    type: string;
    name: string;
    status: string;
    startTime: string;
    endTime: string;
    isCompleted: boolean;
    isViewed: boolean;
    errorMessage?: string;
    knowledgeDocumentId?: string;
  }>>('/tasks/unviewed'),
  markTaskViewed: (taskId: string) => api.post(`/tasks/${taskId}/mark-viewed`),
  markAllTasksViewed: () => api.post('/tasks/mark-all-viewed'),
  getAverageExecutionTime: (taskType: string) => api.get<{
    type: string;
    averageTimeSeconds: number | null;
    averageTimeMinutes: number | null;
    hasData: boolean;
  }>(`/tasks/average-time/${taskType}`),
  // Ретрай в один шаг: сервер сам запускает повторное выполнение
  retryTask: (taskId: string) => api.post(`/tasks/${taskId}/retry`)
};

export const roadmapApi = {
  generate: (data: { goal: string; deadline: string; detailLevel: number }) => 
    api.post('/roadmap/generate', data)
};

export const audienceApi = {
  analyze: (data: { 
    projectDescription: string, 
    audienceName: string, 
    knowledgeItemId?: string,
    answers?: Record<string, string>
  }) => api.post('/audience/analyze', data),
  getQuestions: (data: { 
    projectDescription: string, 
    audienceName: string, 
    knowledgeItemId?: string 
  }) => {
    // Создаем копию данных для запроса
    const requestData = { 
      projectDescription: data.projectDescription,
      audienceName: data.audienceName,
      answers: {},
      // Всегда передаем KnowledgeItemId, но если его нет, то пустую строку
      knowledgeItemId: data.knowledgeItemId || ""
    };
    
    return api.post('/audience/questions', requestData);
  }
};

export const reelsApi = {
  analyzeAccounts: (data: {
    instagramAccounts: string[];
    minimumViews: number;
    isSmartAnalysis: boolean;
    period: {
      number: number;
      unit: string;
    };
  }) => api.post('/reels/analyze-accounts', data),
  analyzeByUrls: (data: {
    urls?: string[];
  }) => api.post('/reels/analyze-by-urls', data)
};

export interface UserSearchResult {
  username: string;
  email: string;
}

// Account API
export const accountApi = {
  searchUsers: async (query: string) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_URL}/account/search-users?query=${encodeURIComponent(query)}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.json();
  },

  changeNickname: async (newNickname: string) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_URL}/account/change-nickname`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ newNickname })
    });
    
    // Если ответ не успешен, пытаемся получить детали ошибки
    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = await response.json();
        console.log('API Error Response:', errorData); // Для отладки
        
        // Проверяем разные варианты полей с сообщением
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.Message) {
          errorMessage = errorData.Message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (jsonError) {
        // Если не удалось распарсить JSON, используем общую ошибку
        console.warn('Could not parse error response as JSON:', jsonError);
      }
      
      throw new Error(errorMessage);
    }
    
    return response.json();
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_URL}/account/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    
    // Если ответ не успешен, пытаемся получить детали ошибки
    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = await response.json();
        // Проверяем разные варианты полей с сообщением
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.Message) {
          errorMessage = errorData.Message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (jsonError) {
        // Если не удалось распарсить JSON, используем общую ошибку
        console.warn('Could not parse error response as JSON:', jsonError);
      }
      
      throw new Error(errorMessage);
    }
    
    return response.json();
  }
};

// Интерфейс для элементов в ответе API
interface KnowledgeItemDto {
  id: string;
  itemType: string;
  fileType: string | null;
  name: string;
  content: string | null;
  children: KnowledgeItemDto[];
  parentId: string | null;
  userId: string;
  isShared: boolean;
  isAuthor?: boolean;
  participantCount?: number;
  participants?: DocumentParticipantDto[];
  createdAt?: string;
  metadata: any;
}

interface DocumentParticipantDto {
  id: string;
  userId: string;
  username: string;
  fullName: string;
  role: string;
  createdAt: string;
}

// Интерфейс для корневого объекта в ответе API
interface KnowledgeRootDto {
  id: string;
  itemType: string;
  fileType: string | null;
  name: string;
  content: string | null;
  children: {
    id: string;
    itemType: string;
    fileType: string | null;
    name: string;
    content: string | null;
    children: any[];
    parentId: string | null;
    userId: string;
  }[];
  parentId: string | null;
  userId: string;
}

// Вспомогательная функция для преобразования элемента API с camelCase-именами полей в KnowledgeItemDto
function mapCamelCaseToKnowledgeItemDto(item: any): KnowledgeItemDto {
  return {
    id: item.id,
    itemType: item.itemType,
    fileType: item.fileType,
    name: item.name,
    content: item.content,
    children: Array.isArray(item.children) 
      ? item.children.map(mapCamelCaseToKnowledgeItemDto) 
      : [],
    parentId: item.parentId,
    userId: item.userId,
    isShared: item.isShared,
    isAuthor: item.isAuthor,
    participantCount: item.participantCount,
    participants: item.participants,
    createdAt: item.createdAt,
    metadata: item.metadata
  };
}

// Вспомогательная функция для преобразования KnowledgeItem в формат API
function mapKnowledgeItemToApi(item: KnowledgeItem): any {
  
  if (!item) {
    return null;
  }
  
  // Преобразуем данные без добавления значений по умолчанию
  const result = {
    id: item.id,
    itemType: item.itemType,
    fileType: item.itemType === 'file' ? item.fileType : null,
    name: item.name,
    content: item.content,
    parentId: item.parentId,
    children: item.children ? item.children.map(child => mapKnowledgeItemToApi(child)) : [],
    metadata: item.metadata
  };
  return result;
}

// Вспомогательная функция для преобразования API элемента в KnowledgeItem
function mapApiItemToKnowledgeItem(item: KnowledgeItemDto): KnowledgeItem {
  // Добавляем логирование входных данных
  
  if (!item) {
    console.error('mapApiItemToKnowledgeItem: Получен пустой объект!');
    return {
      id: '',
      itemType: 'file' as 'file',
      name: 'Ошибка загрузки',
      parentId: null
    };
  }
  
  
  const result: KnowledgeItem = {
    id: item.id || '', // Используем пустую строку, если id не определен
    itemType: (item.itemType === 'folder' ? 'folder' : 'file') as 'file' | 'folder',
    name: item.name,
    fileType: item.fileType || undefined,
    content: item.content || undefined,
    isShared: item.isShared || false,
    isAuthor: item.isAuthor,
    participantCount: item.participantCount,
    participants: item.participants?.map(p => ({
      id: p.id,
      userId: p.userId,
      username: p.username,
      fullName: p.fullName,
      role: p.role,
      createdAt: p.createdAt
    })),
    createdAt: item.createdAt,
    // Рекурсивно преобразуем дочерние элементы
    children: item.children && Array.isArray(item.children) 
      ? item.children.map(child => mapApiItemToKnowledgeItem(child)) 
      : undefined,
    parentId: item.parentId,
    metadata: item.metadata
  };
  
  return result;
}

// --- Local cache helpers for Knowledge items ---
const KNOWLEDGE_CACHE_KEY = 'knowledge_cache_v1';

function readKnowledgeCache(): KnowledgeItem[] | null {
  try {
    const raw = localStorage.getItem(KNOWLEDGE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as KnowledgeItem[];
  } catch {
    return null;
  }
}

function writeKnowledgeCache(data: KnowledgeItem[]): void {
  try {
    localStorage.setItem(KNOWLEDGE_CACHE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota errors etc.
  }
}
// --- End local cache helpers ---

export const knowledgeApi = {
  // Получение структуры дерева
  getItems: async (fileTypes?: string[]) => {
    try {
      const url = fileTypes && fileTypes.length > 0 
        ? `/knowledge?${fileTypes.map(type => `fileTypes=${type}`).join('&')}` 
        : '/knowledge';
      const response = await api.get<KnowledgeRootDto | KnowledgeItemDto[]>(url);
      
      let result: KnowledgeItem[] = [];
      
      // Проверяем, является ли ответ корневым объектом с children
      if (response.data && typeof response.data === 'object' && 'children' in response.data && Array.isArray(response.data.children)) {
        // Преобразуем элементы из children с camelCase-именами в KnowledgeItemDto
        result = response.data.children.map(item => mapCamelCaseToKnowledgeItemDto(item))
          .map(item => mapApiItemToKnowledgeItem(item));
      } 
      // Если ответ уже является массивом
      else if (Array.isArray(response.data)) {
        // Проверяем формат первого элемента, чтобы понять, нужно ли преобразование camelCase
        if (response.data.length > 0 && response.data[0] && typeof response.data[0] === 'object' && 'id' in response.data[0]) {
          result = response.data.map(item => mapCamelCaseToKnowledgeItemDto(item))
            .map(item => mapApiItemToKnowledgeItem(item));
        } else {
          result = response.data.map(item => mapApiItemToKnowledgeItem(item as KnowledgeItemDto));
        }
      }
      

      // Сохраняем в локальный кэш
      if (result.length > 0) {
        writeKnowledgeCache(result);
      }

      return result;
    } catch (error) {
      return [];
    }
  },

  // Чтение из локального кеша
  getCachedItems: (): KnowledgeItem[] | null => {
    return readKnowledgeCache();
  },

  // Прямая запись в кеш (например, после изменений)
  saveItemsToCache: (data: KnowledgeItem[]): void => {
    writeKnowledgeCache(data);
  },
  
  // Получение файла по ID
  getFile: (id: string, forceRefresh: boolean = false) => {
    // Если требуется принудительное обновление, добавляем параметр для обхода кэша
    const url = forceRefresh 
      ? `/knowledge/file/${id}?_t=${Date.now()}` 
      : `/knowledge/file/${id}`;
      
    return api.get<KnowledgeItemDto | any>(url)
      .then(response => {
        
        // Проверяем, нужно ли преобразование из camelCase
        if (response.data && typeof response.data === 'object' && 'id' in response.data) {
          const normalizedData = mapCamelCaseToKnowledgeItemDto(response.data);
          const result = mapApiItemToKnowledgeItem(normalizedData);
          
          // Логируем значение isShared для отладки
          
          return result;
        } else {
          // Если данные уже в нужном формате
          const result = mapApiItemToKnowledgeItem(response.data);
          return result;
        }
      })
      .catch(error => {
        throw error;
      });
  },
  
  // Сохранение или обновление элемента
  save: (data: KnowledgeItem) => {
    
    // Проверим, что получили корректные данные
    if (!data) {
      return Promise.reject(new Error('Пустой объект для сохранения'));
    }
    
    // Проверяем наличие обязательных полей
    if (!data.name) {
      return Promise.reject(new Error('Отсутствует название элемента (name)'));
    }
    
    if (!data.itemType) {
      return Promise.reject(new Error('Отсутствует тип элемента (itemType)'));
    }
    
    if (data.itemType === 'file' && !data.fileType) {
      return Promise.reject(new Error('Отсутствует тип файла (fileType) для элемента типа file'));
    }
    
    // Сохраняем оригинальное содержимое для проверки ответа сервера
    const originalContent = data.content;
    
    // Преобразуем данные для сервера без добавления значений по умолчанию
    const serverData = mapKnowledgeItemToApi(data);
    
    // Дополнительные проверки после маппинга
    if (!serverData.name || !serverData.itemType) {
      return Promise.reject(new Error('Критические поля отсутствуют после маппинга'));
    }
    
    // Явно задаем HTTP-заголовки и настройки запроса
    return api.post<KnowledgeItemDto | any>('/knowledge/save', serverData, {
      headers: {
        'Content-Type': 'application/json'
      },
      transformRequest: [(data, headers) => {
        return JSON.stringify(data);
      }]
    })
      .then(response => {
        
        // Проверяем формат данных ответа
        if (response.data && typeof response.data === 'object') {
          // Проверяем, если данные в camelCase формате
          if ('id' in response.data) {
            const normalizedData = mapCamelCaseToKnowledgeItemDto(response.data);
            return mapApiItemToKnowledgeItem(normalizedData);
          } 
          // Проверяем наличие id в ответе
          else if (!response.data.id) {
            // Если id отсутствует, но есть другие идентификаторы, пробуем их использовать
            if (response.data.Id) {
              const responseAny = response.data as any;
              responseAny.id = responseAny.Id;
            } else if (response.data._id) {
              const responseAny = response.data as any;
              responseAny.id = responseAny._id;
            }
          }
        }
        
        // Создаем результат из ответа
        const result = mapApiItemToKnowledgeItem(response.data);
        
        // ВАЖНОЕ ИСПРАВЛЕНИЕ: проверяем, что контент в ответе соответствует отправленному
        // Если контент отсутствует или пустой, восстанавливаем его из оригинальных данных
        if ((!result.content || result.content === '') && originalContent) {
          result.content = originalContent;
        }
        
        return result;
      })
      .catch(error => {
        throw error;
      });
  },
  
  // Перемещение элемента к новому родителю
  moveItem: (id: string, targetParentId: string | null, itemType?: 'file' | 'folder') => 
    api.post('/knowledge/move', { 
      id, 
      itemType: itemType || 'file',
      targetParentId 
    }).then(response => response.data),
  
  // Удаление элемента
  deleteItem: (id: string) => 
    api.delete(`/knowledge/${id}`).then(response => response.data),
  
  // Сохранение элемента (alias для save метода)
  saveItem: (data: KnowledgeItem) => knowledgeApi.save(data),
  
  // Обновление элемента (альтернатива для save, если требуется отдельный метод)
  updateItem: (id: string, data: KnowledgeItem) => {
    const originalContent = data.content; // Сохраняем оригинальный контент
    const serverData = mapKnowledgeItemToApi(data);
    
    // Используем метод save, так как он теперь поддерживает как создание, так и обновление
    return api.post<KnowledgeItemDto>('/knowledge/save', serverData)
      .then(response => {
        const result = mapApiItemToKnowledgeItem(response.data);
        
        // ВАЖНОЕ ИСПРАВЛЕНИЕ: проверяем, что контент в ответе соответствует отправленному
        // Если контент отсутствует или пустой, восстанавливаем его из оригинальных данных
        if ((!result.content || result.content === '') && originalContent) {
          result.content = originalContent;
        }
        
        return result;
      });
  },

  // Переименование элемента (специализированный метод)
  renameItem: (itemId: string, newName: string) => {
    return api.post<KnowledgeItemDto>('/knowledge/rename', {
      id: itemId,
      newName: newName
    }).then(response => {
      return mapApiItemToKnowledgeItem(response.data);
    });
  }
};

// Экспортируем некоторые типы для обратной совместимости
export type { 
  PostData, 
  BackendContentPlanRequest, 
  ContentPlanGenerationResponse, 
  PostPromptRequest, 
  GenerateImageRequest, 
  GenerateContentRequest,
  PromptResponse,
  GenerateImageResponse,
  GenerateContentResponse,
  RegenerateItemRequest,
  RegenerateItemResponse,
  UpdatePostRequest,
  PostUpdateData,
  DeletePostRequest,
  DeletePostResponse
};

// Объединенный API для работы с контент-планами и постами
export const contentApi = {
  // Методы для работы с контент-планом
  generate: (data: BackendContentPlanRequest) => 
    api.post<ContentPlanGenerationResponse>('content-plan/generate', data),
  
  // Методы для работы с настройками контент-плана
  getContentPlanSettings: (contentPlanId: string) => 
    api.get(`/content-plan/settings/${contentPlanId}`),
  
  updateContentPlanSettings: (data: { contentPlanId: string; settings: any }) => 
    api.put('/content-plan/settings', data),
  
  // Методы для работы с промптами постов
  generateIdeaPrompt: (data: PostPromptRequest) => 
    api.post<PromptResponse>('/content-plan/generate/prompt/idea', data),
  
  generateCoverPrompt: (data: PostPromptRequest) => 
    api.post<PromptResponse>('/content-plan/generate/prompt/cover', data),
  
  generateTitlePrompt: (data: PostPromptRequest) => 
    api.post<PromptResponse>('/content-plan/generate/prompt/title', data),
  
  generateContentPrompt: (data: PostPromptRequest) => 
    api.post<PromptResponse>('/content-plan/generate/prompt/content', data),
  
  // Методы для перегенерации элементов поста
  regenerateIdea: (data: RegenerateItemRequest) => 
    api.post<RegenerateItemResponse>('/content-plan/regenerate/idea', data),
  
  regenerateTitle: (data: RegenerateItemRequest) => 
    api.post<RegenerateItemResponse>('/content-plan/regenerate/title', data),
  
  regenerateContent: (data: RegenerateItemRequest) => 
    api.post<RegenerateItemResponse>('/content-plan/regenerate/content', data),
  
  // Метод для обновления поста в контент-плане
  updatePost: (data: UpdatePostRequest) => 
    api.post<any>('/content-plan/update-post', data),
  
  // Метод для удаления поста из контент-плана
  deletePost: (data: DeletePostRequest) => {
    return api.delete<DeletePostResponse>(`/content-plan/delete`, { data });
  },
  
  // Методы для генерации контента
  generateImage: (data: GenerateImageRequest) => 
    api.post<GenerateImageResponse>('/content-plan/generate/image', data),
  
  generateIdea: (data: GenerateContentRequest) => 
    api.post<GenerateContentResponse>('/content-post/generate-idea', data),
  
  generateTitle: (data: GenerateContentRequest) => 
    api.post<GenerateContentResponse>('/content-post/generate-title', data),
  
  generateContent: (data: GenerateContentRequest) => 
    api.post<GenerateContentResponse>('/content-post/generate-content', data),
  
      generatePost: (request: GeneratePostRequest) => {
        // Формируем тело запроса, включая все переданные поля
        const requestBody: any = { date: request.date };
        if (request.format) {
          requestBody.format = request.format;
        }
        if (request.reelsType) {
          requestBody.reelsType = request.reelsType;
        }
        
        console.log('🎯 API generatePost - отправляем на сервер:', {
          url: `/content-plan/${request.contentPlanId}/generate-post`,
          body: requestBody
        });
        
        return api.post<PostData>(
            `/content-plan/${request.contentPlanId}/generate-post`,
            requestBody
        );
    },

    generatePostByIdea: (request: GeneratePostByIdeaRequest) => {
        // Формируем тело запроса для генерации по идее автора
        const requestBody: any = { 
          date: request.date,
          authorIdea: request.authorIdea
        };
        if (request.referencePostUrl) {
          requestBody.referencePostUrl = request.referencePostUrl;
        }
        if (request.reelsType) {
          requestBody.reelsType = request.reelsType;
        }
        
        console.log('🎯 API generatePostByIdea - отправляем на сервер:', {
          url: `/content-plan/${request.contentPlanId}/generate-post-by-idea`,
          body: requestBody
        });
        
        return api.post<PostData>(
            `/content-plan/${request.contentPlanId}/generate-post-by-idea`,
            requestBody
        );
    },
    
    generateWeek: (request: GenerateWeekRequest) => {
        return api.post<GenerateWeekResponse>(
            `/content-plan/${request.contentPlanId}/generate-week`,
            { 
                additionalInfo: request.additionalInfo, 
                enableDeepResearch: request.enableDeepResearch,
                reelsTemplateId: request.reelsTemplateId,
                youtubeUrl: request.youtubeUrl
            }
        );
    },

    // Метод для добавления нового дня
    addNewDay: (request: AddNewDayRequest) => {
        return api.post<AddNewDayResponse>('/content-plan/add-day', request);
    },
};

// Оставляем для обратной совместимости - депрекейтед
export const contentPlanApi = {
  generate: (data: BackendContentPlanRequest) => contentApi.generate(data)
};

export const contentPostApi = {
  generateIdeaPrompt: (data: PostPromptRequest) => contentApi.generateIdeaPrompt(data),
  generateCoverPrompt: (data: PostPromptRequest) => contentApi.generateCoverPrompt(data),
  generateTitlePrompt: (data: PostPromptRequest) => contentApi.generateTitlePrompt(data),
  generateContentPrompt: (data: PostPromptRequest) => contentApi.generateContentPrompt(data),
  regenerateIdea: (data: RegenerateItemRequest) => contentApi.regenerateIdea(data),
  regenerateTitle: (data: RegenerateItemRequest) => contentApi.regenerateTitle(data),
  regenerateContent: (data: RegenerateItemRequest) => contentApi.regenerateContent(data),
  updatePost: (data: UpdatePostRequest) => contentApi.updatePost(data),
  deletePost: (data: DeletePostRequest) => contentApi.deletePost(data),
  generateImage: (data: GenerateImageRequest) => contentApi.generateImage(data),
  generateIdea: (data: GenerateContentRequest) => contentApi.generateIdea(data),
  generateTitle: (data: GenerateContentRequest) => contentApi.generateTitle(data),
  generateContent: (data: GenerateContentRequest) => contentApi.generateContent(data),
  generatePost: (data: GeneratePostRequest) => contentApi.generatePost(data),
  generatePostByIdea: (data: GeneratePostByIdeaRequest) => contentApi.generatePostByIdea(data)
};

export const fetchContentPlan = async (id: string): Promise<ContentPlanData | null> => {
  try {
    const response = await api.get(`/content-plan/plan/${id}`);
    if (response.data) {
      return response.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Интерфейсы для работы с конспектами
export interface CreateSummaryFromTextRequest {
  title: string;
  text: string;
}

export interface CreateSummaryFromFileRequest {
  title: string;
  file: File;
}

export interface CreateSummaryFromYouTubeRequest {
  title: string;
  youtubeUrl: string;
}

export interface SummaryResponse {
  taskId: string;
  message: string;
}

export interface SupportedFormatsResponse {
  formats: string[];
  maxSizeBytes: number;
}

// API для работы с конспектами
export const summaryApi = {
  // Создание конспекта из файла
  createFromFile: (data: CreateSummaryFromFileRequest) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('file', data.file);
    formData.append('sourceType', '1'); // 1 соответствует SummarySourceType.File
    
    return api.post<SummaryResponse>('/summary/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Создание конспекта из текста
  createFromText: (data: CreateSummaryFromTextRequest) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('text', data.text);
    formData.append('sourceType', '0'); // 0 соответствует SummarySourceType.Text
    
    return api.post<SummaryResponse>('/summary/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Создание конспекта из YouTube видео
  createFromYouTube: (data: CreateSummaryFromYouTubeRequest) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('youtubeUrl', data.youtubeUrl);
    formData.append('sourceType', '2'); // 2 соответствует SummarySourceType.YouTube
    
    return api.post<SummaryResponse>('/summary/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Получение поддерживаемых форматов файлов
  getSupportedFormats: () => 
    api.get<SupportedFormatsResponse>('/summary/supported-formats')
};

// Интерфейсы для работы с лимитами генерации
export interface GenerationStatusResponse {
  canGenerate: boolean;
  errorMessage?: string;
  remaining: number;
  unlimited: boolean;
}

// API для работы с лимитами генерации
export const generationLimitsApi = {
  getStatus: (generationType: string) => api.get<GenerationStatusResponse>(`/generation-limits/status/${generationType}`)
};